import { useState, useMemo } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Search, BookOpen, Plus, Trash2, Save } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostr } from '@nostrify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { AUTHOR_PUBKEY } from '@/lib/categories';

interface GlossaryEntry {
  term: string;
  definition: string;
}

const GLOSSARY_EVENT_KIND = 30078;
const GLOSSARY_D_TAG = 'wtfisbitcoin-glossary';

function useGlossary() {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['glossary'],
    queryFn: async () => {
      const events = await nostr.query([
        {
          kinds: [GLOSSARY_EVENT_KIND],
          authors: [AUTHOR_PUBKEY],
          '#d': [GLOSSARY_D_TAG],
          limit: 1,
        },
      ]);

      if (events.length === 0) {
        return [];
      }

      try {
        return JSON.parse(events[0].content) as GlossaryEntry[];
      } catch {
        return [];
      }
    },
  });
}

function usePublishGlossary() {
  const { mutateAsync: createEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entries: GlossaryEntry[]) => {
      // Sort alphabetically before saving
      const sorted = [...entries].sort((a, b) => 
        a.term.toLowerCase().localeCompare(b.term.toLowerCase(), 'de')
      );

      const event = await createEvent({
        kind: GLOSSARY_EVENT_KIND,
        content: JSON.stringify(sorted),
        tags: [
          ['d', GLOSSARY_D_TAG],
          ['title', 'Bitcoin-Duden'],
        ],
      });

      return event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['glossary'] });
    },
  });
}

export function DudenPage() {
  const { user } = useCurrentUser();
  const isAuthor = user?.pubkey === AUTHOR_PUBKEY;
  const { data: glossary, isLoading } = useGlossary();
  const { mutateAsync: publishGlossary, isPending: isSaving } = usePublishGlossary();

  const [searchTerm, setSearchTerm] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedEntries, setEditedEntries] = useState<GlossaryEntry[]>([]);

  useSeoMeta({
    title: 'Bitcoin-Duden - wtfisBitcoin',
    description: 'Das Bitcoin-Glossar: Alle wichtigen Begriffe rund um Bitcoin einfach erklärt.',
  });

  // Filter entries based on search
  const filteredEntries = useMemo(() => {
    const entries = isEditing ? editedEntries : (glossary || []);
    if (!searchTerm.trim()) return entries;

    const search = searchTerm.toLowerCase();
    return entries.filter(
      (entry) => entry.term.toLowerCase().includes(search)
    );
  }, [glossary, editedEntries, searchTerm, isEditing]);

  const startEditing = () => {
    setEditedEntries(glossary || []);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedEntries([]);
  };

  const saveGlossary = async () => {
    await publishGlossary(editedEntries);
    setIsEditing(false);
  };

  const addEntry = () => {
    setEditedEntries([...editedEntries, { term: '', definition: '' }]);
  };

  const updateEntry = (index: number, field: 'term' | 'definition', value: string) => {
    const updated = [...editedEntries];
    updated[index] = { ...updated[index], [field]: value };
    setEditedEntries(updated);
  };

  const removeEntry = (index: number) => {
    setEditedEntries(editedEntries.filter((_, i) => i !== index));
  };

  return (
    <Layout>
      <div className="container py-8 lg:py-12 max-w-4xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Bitcoin-Duden</h1>
              <p className="text-muted-foreground">Alle Begriffe einfach erklärt</p>
            </div>
          </div>

          {isAuthor && !isEditing && (
            <Button onClick={startEditing} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Bearbeiten
            </Button>
          )}

          {isAuthor && isEditing && (
            <div className="flex gap-2">
              <Button onClick={cancelEditing} variant="outline">
                Abbrechen
              </Button>
              <Button onClick={saveGlossary} disabled={isSaving} className="gap-2">
                <Save className="w-4 h-4" />
                {isSaving ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Begriff suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 py-6 text-lg bg-card border-border/50"
          />
        </div>

        {/* Add new entry button (editing mode) */}
        {isEditing && (
          <Button onClick={addEntry} variant="outline" className="w-full mb-6 gap-2 border-dashed">
            <Plus className="w-4 h-4" />
            Neuen Begriff hinzufügen
          </Button>
        )}

        {/* Glossary entries */}
        {isLoading ? (
          <Card className="border-border/50 bg-card/50">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Lade Bitcoin-Duden...</p>
            </CardContent>
          </Card>
        ) : filteredEntries.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <div className="max-w-md mx-auto space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                  <BookOpen className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">
                  {searchTerm ? 'Kein Begriff gefunden' : 'Noch keine Einträge'}
                </h3>
                <p className="text-muted-foreground">
                  {searchTerm
                    ? 'Versuche einen anderen Suchbegriff.'
                    : isAuthor
                    ? 'Klicke auf "Bearbeiten" um Begriffe hinzuzufügen.'
                    : 'Bald werden hier Bitcoin-Begriffe erklärt.'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : isEditing ? (
          <div className="space-y-4">
            {editedEntries.map((entry, index) => (
              <Card key={index} className="border-border/50 bg-card/50">
                <CardContent className="p-4 space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={entry.term}
                      onChange={(e) => updateEntry(index, 'term', e.target.value)}
                      placeholder="Begriff"
                      className="font-semibold"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeEntry(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Input
                    value={entry.definition}
                    onChange={(e) => updateEntry(index, 'definition', e.target.value)}
                    placeholder="Definition / Erklärung"
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-border/50 bg-card/50 overflow-hidden">
            <div className="divide-y divide-border/50">
              {filteredEntries.map((entry, index) => (
                <div
                  key={index}
                  className="p-4 hover:bg-secondary/30 transition-colors"
                >
                  <dt className="font-semibold text-lg text-primary mb-1">
                    {entry.term}
                  </dt>
                  <dd className="text-muted-foreground">{entry.definition}</dd>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Entry count */}
        {!isLoading && filteredEntries.length > 0 && (
          <p className="text-center text-sm text-muted-foreground mt-6">
            {filteredEntries.length} {filteredEntries.length === 1 ? 'Begriff' : 'Begriffe'}
            {searchTerm && ` für "${searchTerm}"`}
          </p>
        )}
      </div>
    </Layout>
  );
}
